import { useEffect, useRef, useState } from "react";
import { ShieldCheck, Link2, Unlink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/* In-game (CEF) tester link + zero-game-code telemetry probe.
   The tester pastes the Tester ID from their kit email; from then on the menu reports the GPU
   renderer, cores, RAM class, screen and hub round-trip once per launch (plus any perf snapshot the
   UE5 gamebridge exposes as window.ue.gamebridge.perf()). The real per-frame telemetry comes from the
   UE5 client via /api/testing/telemetry/* — see the admin Integration tab. */

const KEY = "nc_tester_token";
export const getTesterToken = () => localStorage.getItem(KEY) || "";

function gpuRenderer() {
  try {
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl2") || c.getContext("webgl");
    const ext = gl?.getExtension("WEBGL_debug_renderer_info");
    return ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : "";
  } catch { return ""; }
}

async function bridgePerf() {
  try { const fn = window.ue?.gamebridge?.perf; if (!fn) return {}; const r = await fn(); return typeof r === "string" ? JSON.parse(r) : (r || {}); } catch { return {}; }
}

export async function sendProbe(sessionId) {
  const token = getTesterToken();
  if (!token) return null;
  const t0 = performance.now();
  await api.get("/testing/ping").catch(() => {});
  const latency = Math.round(performance.now() - t0);
  const body = { gpu_renderer: gpuRenderer(), cpu_cores: navigator.hardwareConcurrency || 0, device_memory_gb: navigator.deviceMemory || 0,
    screen: `${window.screen.width}x${window.screen.height}`, latency_ms: latency, ua: navigator.userAgent.slice(0, 300), bridge_stats: await bridgePerf(), session_id: sessionId || "" };
  const r = await api.post("/testing/telemetry/probe", body, { headers: { "X-Tester-Token": token } });
  return r.data;
}

/** Runs the probe once on mount and every 2 minutes while a Tester ID is linked. */
export function useTesterTelemetry() {
  const session = useRef("");
  useEffect(() => {
    if (!getTesterToken()) return undefined;
    let alive = true;
    const tick = () => sendProbe(session.current).then((r) => { if (alive && r?.session_id) session.current = r.session_id; }).catch(() => {});
    tick();
    const id = setInterval(tick, 120000);
    return () => { alive = false; clearInterval(id); };
  }, []);
}

export function TesterLinkPanel({ audio, accent = "#2E6BFF" }) {
  const [token, setToken] = useState(getTesterToken());
  const [input, setInput] = useState("");
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (token) api.get("/testing/me", { headers: { "X-Tester-Token": token } }).then((r) => setMe(r.data)).catch(() => setMe(null)); }, [token]);

  const link = async () => {
    const v = input.trim();
    if (v.length < 16) { toast.error("Paste the full Tester ID from your kit email"); return; }
    setBusy(true);
    try {
      const r = await api.get("/testing/me", { headers: { "X-Tester-Token": v } });
      localStorage.setItem(KEY, v); setToken(v); setMe(r.data); setInput(""); audio?.click?.();
      toast.success(`Linked as ${r.data.tester.name} — telemetry probe running`);
      sendProbe("").catch(() => {});
    } catch (e) { audio?.deny?.(); toast.error(e.response?.data?.detail || "Unknown Tester ID"); }
    finally { setBusy(false); }
  };
  const unlink = () => { localStorage.removeItem(KEY); setToken(""); setMe(null); audio?.back?.(); toast.success("Tester ID unlinked"); };

  return (
    <div data-testid="settings-testing">
      <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-400 flex items-center gap-2"><ShieldCheck className="w-4 h-4" style={{ color: accent }} /> Test program telemetry</p>
      {token && me ? (
        <div className="mt-4 border border-white/10 bg-black/40 p-4">
          <p data-testid="testing-linked-name" className="font-mono text-sm text-zinc-100">Linked: <b>{me.tester.name}</b> · <span className="uppercase text-[10px] tracking-[0.2em]" style={{ color: accent }}>{me.tester.status}</span></p>
          <p className="font-mono text-[11px] text-zinc-500 mt-2 leading-relaxed">{me.tester.consent?.telemetry ? "Performance telemetry is ON — FPS, frame times, ping, VRAM/RAM, hitches, zone and player count are sent while you play. Never files or keystrokes." : "Telemetry is PAUSED from your portal — nothing is recorded."}</p>
          <p className="font-mono text-[11px] text-zinc-500 mt-1">Server: {me.program.server_name} · Sessions: {me.tester.stats?.sessions || 0}</p>
          <button data-testid="testing-unlink" onClick={unlink} className="mt-3 font-mono text-[9px] tracking-[0.2em] uppercase px-3 py-1.5 border border-white/15 text-zinc-300 hover:border-white/40 transition-colors flex items-center gap-1.5"><Unlink className="w-3 h-3" /> Unlink</button>
        </div>
      ) : (
        <div className="mt-4">
          <p className="font-mono text-[11px] text-zinc-500 leading-relaxed">Part of the Major Update Test Program? Paste the Tester ID from your kit email so your performance data links to you. Not a tester yet? Apply at specpixel.com/testing.</p>
          <div className="flex gap-2 mt-3">
            <input data-testid="testing-token-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Tester ID (32 characters)" className="flex-1 bg-black/60 border border-white/15 px-3 py-2 font-mono text-xs text-zinc-100 focus:outline-none" style={{ borderColor: input ? accent : undefined }} />
            <button data-testid="testing-link" disabled={busy} onClick={link} className="font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2 text-black disabled:opacity-50 flex items-center gap-1.5" style={{ backgroundColor: accent }}><Link2 className="w-3 h-3" /> Link</button>
          </div>
        </div>
      )}
    </div>
  );
}
